import { useScenarioValidator } from "@/composables/validation/scenarioValidator";
import { computed } from "vue";
import type {
  HypotheticalFrequency,
  PredictiveCalcs,
  PumpTornadoChartContext,
  StartupTdhReportCalcRow
} from "@/libs/charting";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";

const getPredictedRows = (
  destructuredScenario: DestructuredScenario,
  predictedCalcs: PredictiveCalcs,
  hypotheticalPumpFrequenciesHz: HypotheticalFrequency[]
): StartupTdhReportCalcRow[] => {
  const frequenciesToKeep = hypotheticalPumpFrequenciesHz.map(
    hf => hf.frequency
  );
  return Object.values(predictedCalcs)
    .filter(
      // remove the frequency at operating point
      c => frequenciesToKeep.indexOf(c.hypotheticalCalcs.pumpHz.value) > -1
    )
    .sort(
      (a, z) =>
        z.hypotheticalCalcs.pumpHz.value - a.hypotheticalCalcs.pumpHz.value
    )
    .map(c => {
      const { errors } = useScenarioValidator(
        destructuredScenario,
        c.hypotheticalCalcs
      );

      // Errors will cause the report to display N/A values.
      // This should match the errors that disable the Calc Flow button
      const predictionErrors = computed(() =>
        errors.value.filter(e =>
          [
            "pip_range",
            "pump_outside_graphing_range",
            "target_rate_above_max_flow_rate"
          ].includes(e.name)
        )
      );

      if (
        isNaN(c.hypotheticalCalcs.desiredLiquidRateSTDB.value) ||
        predictionErrors.value.length > 0 ||
        isNaN(
          c.hypotheticalCalcs.pumpCalculations.aggregate
            .requiredTotalDynamicHeadFt.value
        ) ||
        c.predictedFlowRateDataSet.status !== "result-found"
      ) {
        return {
          pumpHz: c.hypotheticalCalcs.adjustedPumpHzFrequency.value,
          liquidFlowRateSTBD: NaN,
          oilFlowRateSTBD: NaN,
          waterFlowRateSTBD: NaN,
          gasFlowRateSCFD: NaN,
          pumpIntakePressurePSIA: NaN,
          dischargePressurePSIA: NaN,
          producedTotalDynamicHeadFt: NaN
        };
      }

      // Whenever US Water Well/Europe Geothermal/Turkey Geothermal is selected
      // as the unit type for a scenario, the reports pages should exclude any oil references

      return {
        pumpHz: c.hypotheticalCalcs.adjustedPumpHzFrequency.value,
        liquidFlowRateSTBD:
          c.hypotheticalCalcs.surfaceFlowRates.value.liquidFlowRateSTBD,
        oilFlowRateSTBD:
          c.hypotheticalCalcs.surfaceFlowRates.value.oilFlowRateSTBD,
        waterFlowRateSTBD:
          c.hypotheticalCalcs.surfaceFlowRates.value.waterFlowRateSTBD,
        gasFlowRateSCFD:
          c.hypotheticalCalcs.surfaceFlowRates.value.gasFlowRateSCFD,
        pumpIntakePressurePSIA:
          c.hypotheticalCalcs.pumpIntakePressurePSIA.value,
        dischargePressurePSIA:
          c.hypotheticalCalcs.pumpCalculations.aggregate
            .producedDischargePressurePSIA.value,
        producedTotalDynamicHeadFt:
          c.hypotheticalCalcs.pumpCalculations.aggregate
            .producedTotalDynamicHeadFt.value
      };
    });
};

export const getStartupTdhRows = (
  context: PumpTornadoChartContext,
  predictedCalcs: PredictiveCalcs,
  destructuredScenario: DestructuredScenario
) => {
  const predictedRows = getPredictedRows(
    destructuredScenario,
    predictedCalcs,
    context.hypotheticalPumpFrequenciesHz
  );

  const designRow = {
    pumpHz: context.scenarioCalculations.adjustedPumpHzFrequency.value,
    liquidFlowRateSTBD:
      context.scenarioCalculations.surfaceFlowRates.value.liquidFlowRateSTBD,
    oilFlowRateSTBD:
      context.scenarioCalculations.surfaceFlowRates.value.oilFlowRateSTBD,
    waterFlowRateSTBD:
      context.scenarioCalculations.surfaceFlowRates.value.waterFlowRateSTBD,
    gasFlowRateSCFD:
      context.scenarioCalculations.surfaceFlowRates.value.gasFlowRateSCFD,
    pumpIntakePressurePSIA:
      context.scenarioCalculations.pumpIntakePressurePSIA.value,
    dischargePressurePSIA:
      context.scenarioCalculations.pumpCalculations.aggregate
        .producedDischargePressurePSIA.value,
    producedTotalDynamicHeadFt:
      context.scenarioCalculations.pumpCalculations.aggregate
        .producedTotalDynamicHeadFt.value,
    operatingRPM:
      context.scenarioCalculations.pumpCalculations.aggregate.operatingRPM.value
  };

  return [designRow, ...predictedRows];
};
